import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { getMyMenu } from '@/apis/userAuthApi'
import { getListMenuFail, getListMenuSuccess } from '@/redux/ducks/menuDucks'
import { tokenStorage } from '@/services/tokenStorage'
import { normalizeListResponse } from '@/utils/apiResponseUtils'
import { processWebMenu, type MenuLike } from '@/utils/menuUtils'

type SsoMenuStatus = 'idle' | 'loading' | 'ok' | 'unauthorized' | 'error'

export type SsoMenuContextValue = {
  status: SsoMenuStatus
  refetch: () => void
}

const defaultValue: SsoMenuContextValue = {
  status: 'idle',
  refetch: () => {},
}

const SsoMenuContext = createContext<SsoMenuContextValue>(defaultValue)

const emptyMenu = {
  webUrls: [] as string[],
  webChucNangs: [] as Array<{ url: string; chucNangs: string[] }>,
  webChucNangAll: [] as string[],
}

const MENU_CACHE_KEY = 'ssoMenuCache'

/** Menu phụ thuộc user + dự án + chi nhánh trong JWT; refresh token không làm mất cache. */
const menuCacheKey = (token: string): string => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return `${payload.sub}:${payload.duAnId}:${payload.chiNhanhId}`
  } catch {
    return token
  }
}

const readMenuCache = (key: string): MenuLike[] | null => {
  try {
    const parsed = JSON.parse(localStorage.getItem(MENU_CACHE_KEY) ?? 'null') as { key?: string; data?: unknown }
    return parsed?.key === key && Array.isArray(parsed.data) ? (parsed.data as MenuLike[]) : null
  } catch {
    return null
  }
}

const writeMenuCache = (key: string, data: MenuLike[]) => {
  try {
    localStorage.setItem(MENU_CACHE_KEY, JSON.stringify({ key, data }))
  } catch {
    /* ignore quota */
  }
}

let inflightMenu: { key: string; promise: Promise<MenuLike[]> } | null = null

/** Gộp các lần gọi trùng (StrictMode, nhiều effect) thành 1 request — API proxy sang auth service rất chậm. */
const fetchMenuOnce = (key: string): Promise<MenuLike[]> => {
  if (inflightMenu?.key === key) return inflightMenu.promise
  const promise = (async () => {
    const res = (await getMyMenu()) as { data?: MenuLike[] }
    return normalizeListResponse<MenuLike>(res).data
  })()
  inflightMenu = { key, promise }
  const clear = () => {
    if (inflightMenu?.promise === promise) inflightMenu = null
  }
  promise.then(clear, clear)
  return promise
}

export function SsoMenuProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const [status, setStatus] = useState<SsoMenuStatus>('idle')
  const [reloadKey, setReloadKey] = useState(0)
  const statusRef = useRef<SsoMenuStatus>(status)
  statusRef.current = status
  const freshKeyRef = useRef<string | null>(null)
  const handledReloadKeyRef = useRef(reloadKey)

  const refetch = useCallback(() => {
    setReloadKey((key) => key + 1)
  }, [])

  useEffect(() => {
    const token = tokenStorage.getAccessToken()
    const userType = tokenStorage.getUserType()
    if (!token || userType !== 'user') {
      freshKeyRef.current = null
      setStatus('idle')
      dispatch(getListMenuFail(emptyMenu))
      return
    }

    const key = menuCacheKey(token)
    const force = handledReloadKeyRef.current !== reloadKey
    handledReloadKeyRef.current = reloadKey
    // Đã tải menu mới cho phiên này → đổi trang không gọi lại.
    if (!force && freshKeyRef.current === key) return

    let cancelled = false
    const cached = statusRef.current === 'ok' ? null : readMenuCache(key)
    if (cached) {
      dispatch(getListMenuSuccess(processWebMenu(cached)))
      setStatus('ok')
    } else {
      setStatus((prev) => (prev === 'ok' ? prev : 'loading'))
    }
    ;(async () => {
      try {
        const data = await fetchMenuOnce(key)
        writeMenuCache(key, data)
        if (cancelled) return
        freshKeyRef.current = key
        dispatch(getListMenuSuccess(processWebMenu(data)))
        setStatus('ok')
      } catch (err: unknown) {
        if (cancelled) return
        const statusCode = (err as { statusCode?: number })?.statusCode
        if (statusCode === 401) {
          localStorage.removeItem(MENU_CACHE_KEY)
          setStatus('unauthorized')
          dispatch(getListMenuFail(emptyMenu))
          return
        }
        // Đã có menu từ lần trước → giữ nguyên, tránh lỗi mạng thoáng qua làm mất hết quyền.
        if (statusRef.current === 'ok') return
        setStatus('error')
        dispatch(getListMenuFail(emptyMenu))
      }
    })()

    return () => {
      cancelled = true
    }
  }, [dispatch, pathname, reloadKey])

  const value = useMemo<SsoMenuContextValue>(() => ({ status, refetch }), [status, refetch])

  return <SsoMenuContext.Provider value={value}>{children}</SsoMenuContext.Provider>
}

export function useSsoMenu(): SsoMenuContextValue {
  return useContext(SsoMenuContext)
}
